import { useEffect, useState } from "react";
import { Mail, Loader2, Sparkles, MailQuestion, ChevronDown, Send, ExternalLink, Link as LinkIcon } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const INTENT_STYLE = {
  interested:        { bg: "bg-[#E8F5E9]", fg: "text-[#1B5E20]", label: "Interested" },
  asking_questions:  { bg: "bg-[#E3F2FD]", fg: "text-[#0D47A1]", label: "Asking" },
  not_interested:    { bg: "bg-[#FFEBEE]", fg: "text-[#B71C1C]", label: "Not interested" },
  wrong_person:      { bg: "bg-[#FFF3E0]", fg: "text-[#E65100]", label: "Wrong person" },
  out_of_office:     { bg: "bg-ink-100",   fg: "text-ink-600",  label: "Out of office" },
  unsubscribe:       { bg: "bg-ink-900",   fg: "text-white",    label: "Unsubscribe" },
  other:             { bg: "bg-ink-100",   fg: "text-ink-600",  label: "Other" },
};

export default function OutreachInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/outreach/replies");
      setItems(data.replies || []);
    } catch (e) {
      toast.error("Could not load replies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const [syncing, setSyncing] = useState(false);
  const onSync = async () => {
    setSyncing(true);
    try {
      const { data } = await http.post("/outreach/imap/sync");
      if (data.new_replies > 0) {
        toast.success(`Synced — ${data.new_replies} new repl${data.new_replies === 1 ? "y" : "ies"} captured`);
      } else {
        toast.info(`Synced — no new replies (${data.polled_messages} message${data.polled_messages === 1 ? "" : "s"} scanned)`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-ink-500">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading replies…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-16 text-center" data-testid="inbox-empty">
        <div className="inline-grid place-items-center w-12 h-12 rounded-sm bg-ink-100 mb-3">
          <MailQuestion className="w-5 h-5 text-ink-400" />
        </div>
        <div className="font-display text-xl font-extrabold text-ink-900 mb-1">No replies yet</div>
        <p className="text-sm text-ink-500 max-w-md mx-auto mb-4">
          We poll your IMAP inbox every 5 minutes. Replies to emails you sent through LeadIQ
          will appear here automatically. You can also force a sync now.
        </p>
        <Button
          onClick={onSync}
          disabled={syncing}
          variant="outline"
          className="rounded-sm h-9"
          data-testid="inbox-sync-now"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Sync replies now
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="inbox-list">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-widestx font-mono text-ink-400">
          {items.length} repl{items.length === 1 ? "y" : "ies"} captured
        </div>
        <Button
          onClick={onSync}
          disabled={syncing}
          variant="outline"
          className="rounded-sm h-8 text-xs"
          data-testid="inbox-sync-now"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
          Sync replies now
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((r) => (
          <ReplyRow
            key={r.id}
            reply={r}
            open={openId === r.id}
            onToggle={() => setOpenId(openId === r.id ? null : r.id)}
            onRefresh={load}
          />
        ))}
      </div>
    </div>
  );
}

function ReplyRow({ reply, open, onToggle, onRefresh }) {
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(reply.ai_suggestions || []);
  const [intent, setIntent] = useState(reply.ai_intent);
  const [summary, setSummary] = useState(reply.ai_summary);
  const intentStyle = INTENT_STYLE[intent] || INTENT_STYLE.other;
  const [composing, setComposing] = useState(null); // index of suggestion being edited
  const [composeBody, setComposeBody] = useState("");

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await http.post(`/outreach/replies/${reply.id}/suggest`);
      setSuggestions(data.suggestions || []);
      setIntent(data.intent);
      setSummary(data.summary);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not suggest replies");
    } finally {
      setSuggesting(false);
    }
  };

  const sendReply = async () => {
    // Reply uses the same /outreach/send flow but creates a quick draft first
    try {
      // Quick draft persistence for traceability — just send via SMTP
      // We need a fresh draft entry: create one via 'send' shortcut.
      // Reusing /outreach/send with a placeholder draft_id won't work; create a draft row first.
      const draftRes = await http.post("/outreach/replies/" + reply.id + "/send", {
        body: composeBody,
      });
      toast.success("Reply sent");
      setComposing(null);
      setComposeBody("");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    }
  };

  return (
    <div className="bg-white border border-ink-100 rounded-sm overflow-hidden" data-testid={`reply-${reply.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-ink-50/50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[9px] uppercase tracking-widestx font-bold px-2 py-0.5 rounded-sm ${intentStyle.bg} ${intentStyle.fg}`}>
              {intentStyle.label}
            </span>
            <div className="font-medium text-ink-900 text-sm truncate">{reply.subject || "(no subject)"}</div>
          </div>
          <div className="text-[11px] font-mono text-ink-500 truncate">
            From {reply.from} · {new Date(reply.received_at).toLocaleString()}
          </div>
          {summary && <div className="text-xs text-ink-600 mt-1 italic line-clamp-1">"{summary}"</div>}
        </div>
        <ChevronDown className={`w-4 h-4 text-ink-400 ml-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-ink-100 px-5 py-4 bg-ink-50/30">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
            Their message
          </div>
          <pre className="text-sm text-ink-800 whitespace-pre-wrap font-sans leading-relaxed mb-5 max-h-60 overflow-auto">
            {reply.body || "(no body)"}
          </pre>

          {!suggestions.length && (
            <Button
              onClick={suggest}
              disabled={suggesting}
              data-testid={`suggest-${reply.id}`}
              className="h-10 px-4 rounded-sm bg-ink-900 hover:bg-ink-800 text-white"
            >
              {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Sparkles className="w-4 h-4 mr-2" /> AI-suggest 3 replies</>)}
            </Button>
          )}

          {suggestions.length > 0 && composing === null && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                Suggested replies
              </div>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setComposing(i); setComposeBody(s.body || ""); }}
                  data-testid={`suggestion-${reply.id}-${i}`}
                  className="w-full text-left p-4 border border-ink-100 hover:border-ink-900 rounded-sm bg-white transition-colors group"
                >
                  <div className="font-semibold text-sm text-ink-900 mb-1 flex items-center justify-between">
                    {s.label}
                    <ExternalLink className="w-3 h-3 text-ink-300 group-hover:text-ink-900" />
                  </div>
                  <div className="text-[12px] text-ink-600 line-clamp-3">{s.body}</div>
                </button>
              ))}
            </div>
          )}

          {composing !== null && (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                  Edit before sending
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const url = window.prompt(
                      "Paste a link to insert (Calendly, demo, case study URL, etc.):",
                      "https://",
                    );
                    if (!url || url === "https://") return;
                    const safe = url.startsWith("http") ? url : `https://${url}`;
                    setComposeBody((b) => (b ? `${b.replace(/\s+$/, "")}\n\n${safe}\n` : `${safe}\n`));
                    toast.success("Link added — clicks will be tracked automatically.");
                  }}
                  data-testid={`reply-add-link-${reply.id}`}
                  className="text-[10px] uppercase tracking-widestx font-semibold text-brand hover:text-brand-hover inline-flex items-center gap-1"
                >
                  <LinkIcon className="w-3 h-3" />
                  Add tracked link
                </button>
              </div>
              <Textarea
                rows={10}
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                className="font-mono text-sm rounded-sm"
                data-testid={`compose-${reply.id}`}
              />
              <p className="text-[11px] text-ink-500 mt-2 leading-relaxed">
                URLs you include are auto-rewritten to tracked links so clicks land in the Dashboard.
              </p>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => setComposing(null)}
                  variant="outline"
                  className="h-10 px-4 rounded-sm border-ink-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendReply}
                  className="h-10 px-4 rounded-sm bg-brand hover:bg-brand-hover text-white flex-1"
                  data-testid={`send-reply-${reply.id}`}
                >
                  <Send className="w-4 h-4 mr-2" /> Send via your SMTP
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
